import React, { useState } from 'react';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import ColorSwatch from './components/ColorSwatch';
import TypographyExample from './components/TypographyExample';
import SpacingExample from './components/SpacingExample';
import ComponentPreview from './components/ComponentPreview';
import IconUsageRules from './components/IconUsageRules';
import Footer from '../../components/Footer';
import TopNavigation from '../../components/TopNavigation';

const AdminDesignSystem = () => {
  const [buttonVariant, setButtonVariant] = useState('default');
  const [buttonSize, setButtonSize] = useState('default');

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Palette" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Design System</h1>
                <p className="text-sm text-muted-foreground">Read-only reference for Sleeve's live design system</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-muted/50 border border-border">
              <Icon name="Info" size={16} color="var(--color-primary)" />
              <p className="text-sm text-muted-foreground">
                This page documents the current, live design system used across the Sleeve platform. All tokens and components shown here reflect production values.
              </p>
            </div>
          </div>

          {/* Section 1: Color System */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Color System</h2>
              <p className="text-sm text-muted-foreground">Core color palette with hex values and usage guidelines</p>
            </div>

            {/* Backgrounds */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Backgrounds</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ColorSwatch 
                  name="Primary Background" 
                  token="--color-background" 
                  hex="#242423" 
                  usage="Main page background"
                />
                <ColorSwatch 
                  name="Card / Surface" 
                  token="--color-card" 
                  hex="#2E2E2C" 
                  usage="Elevated surfaces, cards"
                />
                <ColorSwatch 
                  name="Muted Background" 
                  token="--color-muted" 
                  hex="#2E2E2C" 
                  usage="Subtle backgrounds, disabled states"
                />
                <ColorSwatch 
                  name="Border" 
                  token="--color-border" 
                  hex="#3A3A38" 
                  usage="Borders, dividers, input outlines"
                />
              </div>
            </div>

            {/* Text Colors */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Text Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ColorSwatch 
                  name="Primary Text" 
                  token="--color-foreground" 
                  hex="#E8EDDF" 
                  usage="Headings, body text, primary content"
                />
                <ColorSwatch 
                  name="Secondary Text" 
                  token="--color-text-secondary" 
                  hex="#CFDBD5" 
                  usage="Supporting text, descriptions"
                />
                <ColorSwatch 
                  name="Muted Text" 
                  token="--color-muted-foreground" 
                  hex="#9CA3AF" 
                  usage="Helper text, placeholders, labels"
                />
              </div>
            </div>

            {/* Brand Accents */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Brand Accents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ColorSwatch 
                  name="Primary (Teal)" 
                  token="--color-primary" 
                  hex="#4FB3B8" 
                  usage="Primary actions, links, focus states"
                />
                <ColorSwatch 
                  name="Secondary (Saffron)" 
                  token="--color-secondary" 
                  hex="#F5CB5C" 
                  usage="Secondary actions, emphasis, highlights"
                />
                <ColorSwatch 
                  name="Tertiary (Coral)" 
                  token="--color-tertiary" 
                  hex="#FF6B6B" 
                  usage="Tertiary accent for critical emphasis, destructive actions, and high-attention UI moments. Use sparingly."
                />
              </div>
              <div className="mt-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-muted/30 border border-border">
                <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Color Sharing:</strong> Coral is intentionally shared between Brand Accent (Tertiary) and Status → Error / Destructive to avoid color proliferation while maintaining semantic clarity.
                </p>
              </div>
            </div>

            {/* Status Colors */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Status & Emphasis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ColorSwatch 
                  name="Success" 
                  token="--color-success" 
                  hex="#4FB3B8" 
                  usage="Success messages, confirmations"
                />
                <ColorSwatch 
                  name="Warning" 
                  token="--color-warning" 
                  hex="#F5CB5C" 
                  usage="Warnings, alerts, cautions"
                />
                <ColorSwatch 
                  name="Error / Destructive" 
                  token="--color-destructive" 
                  hex="#FF6B6B" 
                  usage="Errors, destructive actions only"
                />
              </div>
            </div>
          </section>

          {/* Section 2: Typography */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Typography</h2>
              <p className="text-sm text-muted-foreground">Font family, weights, sizes, and usage examples</p>
            </div>

            {/* Font Family */}
            <div className="mb-8 p-6 rounded-lg bg-card border border-border">
              <h3 className="text-lg font-medium text-foreground mb-3">Font Family</h3>
              <div className="flex items-baseline gap-4 mb-2">
                <span className="text-4xl font-semibold text-foreground">IBM Plex Sans</span>
              </div>
              <p className="text-sm text-muted-foreground">Primary typeface with system-ui fallback</p>
              <code className="text-xs text-primary mt-2 block">font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif</code>
            </div>

            {/* Headings */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Headings</h3>
              <div className="space-y-4">
                <TypographyExample 
                  level="H1" 
                  size="36px" 
                  weight="600 (Semibold)" 
                  lineHeight="1.2"
                  example="Building Reliable Systems"
                  className="text-2xl font-semibold"
                />
                <TypographyExample 
                  level="H2" 
                  size="28px" 
                  weight="600 (Semibold)" 
                  lineHeight="1.3"
                  example="Core Capabilities"
                  className="text-xl font-semibold"
                />
                <TypographyExample 
                  level="H3" 
                  size="20px" 
                  weight="600 (Semibold)" 
                  lineHeight="1.4"
                  example="System Integration"
                  className="text-lg font-semibold"
                />
                <TypographyExample 
                  level="H4" 
                  size="16px" 
                  weight="600 (Semibold)" 
                  lineHeight="1.5"
                  example="Technical Foundations"
                  className="text-base font-semibold"
                />
              </div>
            </div>

            {/* Body Text */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Body Text</h3>
              <div className="space-y-4">
                <TypographyExample 
                  level="Body (Default)" 
                  size="16px" 
                  weight="400 (Regular)" 
                  lineHeight="1.5"
                  example="Orchestrate distributed intelligence across cloud, edge, and legacy systems with operational clarity."
                  className="text-base font-normal"
                />
                <TypographyExample 
                  level="Body (Medium)" 
                  size="16px" 
                  weight="500 (Medium)" 
                  lineHeight="1.5"
                  example="Enhanced body text for emphasis and navigation labels."
                  className="text-base font-medium"
                />
              </div>
            </div>

            {/* Small Text */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Small & Helper Text</h3>
              <div className="space-y-4">
                <TypographyExample 
                  level="Small" 
                  size="14px" 
                  weight="400 (Regular)" 
                  lineHeight="1.5"
                  example="Helper text, descriptions, and secondary information."
                  className="text-sm font-normal"
                />
                <TypographyExample 
                  level="Extra Small" 
                  size="12px" 
                  weight="400 (Regular)" 
                  lineHeight="1.5"
                  example="Captions, labels, and metadata."
                  className="text-xs font-normal"
                />
              </div>
            </div>

            {/* Button Text */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Button Text</h3>
              <div className="space-y-4">
                <TypographyExample 
                  level="Button (Default)" 
                  size="14px" 
                  weight="500 (Medium)" 
                  lineHeight="1"
                  example="Get Started"
                  className="text-sm font-medium"
                />
                <TypographyExample 
                  level="Button (Large)" 
                  size="16px" 
                  weight="500 (Medium)" 
                  lineHeight="1"
                  example="Book a Demo"
                  className="text-base font-medium"
                />
              </div>
            </div>
          </section>

          {/* Section 3: Spacing & Layout */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Spacing & Layout</h2>
              <p className="text-sm text-muted-foreground">Base spacing unit and common spacing patterns</p>
            </div>

            {/* Base Unit */}
            <div className="mb-8 p-6 rounded-lg bg-card border border-border">
              <h3 className="text-lg font-medium text-foreground mb-3">Base Spacing Unit</h3>
              <div className="flex items-baseline gap-4 mb-2">
                <span className="text-4xl font-semibold text-primary">4px</span>
              </div>
              <p className="text-sm text-muted-foreground">All spacing values are multiples of 4px for consistent rhythm</p>
            </div>

            {/* Spacing Scale */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-foreground mb-4">Spacing Scale</h3>
              <div className="space-y-3">
                <SpacingExample token="spacing-1" pixels="4px" size="4px" multiplier="1" usage="Tight spacing, icon gaps" />
                <SpacingExample token="spacing-2" pixels="8px" size="8px" multiplier="2" usage="Small gaps, compact layouts" />
                <SpacingExample token="spacing-3" pixels="12px" size="12px" multiplier="3" usage="Default gap between related items" />
                <SpacingExample token="spacing-4" pixels="16px" size="16px" multiplier="4" usage="Standard spacing, card padding" />
                <SpacingExample token="spacing-6" pixels="24px" size="24px" multiplier="6" usage="Section spacing, larger gaps" />
                <SpacingExample token="spacing-8" pixels="32px" size="32px" multiplier="8" usage="Component separation" />
                <SpacingExample token="spacing-12" pixels="48px" size="48px" multiplier="12" usage="Section padding (mobile)" />
                <SpacingExample token="spacing-16" pixels="64px" size="64px" multiplier="16" usage="Section padding (desktop)" />
              </div>
            </div>

            {/* Section Padding Patterns */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Section Padding Patterns</h3>
              <div className="space-y-3 p-6 rounded-lg bg-card border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Mobile (py)</span>
                  <code className="text-xs text-primary">48px - 64px</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Tablet (py)</span>
                  <code className="text-xs text-primary">64px - 80px</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Desktop (py)</span>
                  <code className="text-xs text-primary">80px - 96px</code>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Grid & Layout System */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Grid & Layout System</h2>
              <p className="text-sm text-muted-foreground">Responsive grid patterns and layout behavior</p>
            </div>

            <div className="space-y-6">
              {/* Max Content Width */}
              <div className="p-6 rounded-lg bg-card border border-border">
                <h3 className="text-lg font-medium text-foreground mb-3">Max Content Width</h3>
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-3xl font-semibold text-primary">1280px</span>
                  <span className="text-sm text-muted-foreground">(max-w-7xl)</span>
                </div>
                <p className="text-sm text-muted-foreground">Primary container width with responsive padding</p>
              </div>

              {/* Column Behavior */}
              <div className="p-6 rounded-lg bg-card border border-border">
                <h3 className="text-lg font-medium text-foreground mb-4">Column Behavior</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Mobile</span>
                    <code className="text-xs text-primary">1 column (grid-cols-1)</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Tablet</span>
                    <code className="text-xs text-primary">2 columns (md:grid-cols-2)</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Desktop</span>
                    <code className="text-xs text-primary">3-4 columns (lg:grid-cols-3/4)</code>
                  </div>
                </div>
              </div>

              {/* Card Grids */}
              <div className="p-6 rounded-lg bg-card border border-border">
                <h3 className="text-lg font-medium text-foreground mb-3">Card Grid Patterns</h3>
                <p className="text-sm text-muted-foreground mb-3">Standard gap between cards: 24px (gap-6)</p>
                <code className="text-xs text-primary block">grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6</code>
              </div>
            </div>
          </section>

          {/* Section 5: Core Components */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Core Components</h2>
              <p className="text-sm text-muted-foreground">Live component previews with usage examples</p>
            </div>

            {/* Buttons */}
            <ComponentPreview title="Buttons" description="Primary, secondary, and text button variants">
              <div className="flex flex-wrap items-center gap-4">
                <Button variant="default" size="default">Primary Button</Button>
                <Button variant="secondary" size="default">Secondary Button</Button>
                <Button variant="outline" size="default">Outline Button</Button>
                <Button variant="ghost" size="default">Ghost Button</Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <Button variant="default" size="sm">Small</Button>
                <Button variant="default" size="default">Default</Button>
                <Button variant="default" size="lg">Large</Button>
              </div>
            </ComponentPreview>

            {/* Inputs */}
            <ComponentPreview title="Input Fields" description="Text inputs with label and placeholder">
              <div className="space-y-4 max-w-md">
                <Input 
                  label="Email Address" 
                  type="email" 
                  placeholder="you@example.com"
                />
                <Input 
                  label="Full Name" 
                  type="text" 
                  placeholder="John Doe"
                />
              </div>
            </ComponentPreview>

            {/* Select */}
            <ComponentPreview title="Select Dropdown" description="Dropdown selection component">
              <div className="max-w-md">
                <Select 
                  label="Choose an option"
                  options={[
                    { value: 'option1', label: 'Option 1' },
                    { value: 'option2', label: 'Option 2' },
                    { value: 'option3', label: 'Option 3' }
                  ]}
                />
              </div>
            </ComponentPreview>

            {/* Checkbox */}
            <ComponentPreview title="Checkbox" description="Checkbox input with label">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="terms" />
                  <label htmlFor="terms" className="text-sm text-foreground cursor-pointer">
                    I agree to the terms and conditions
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="newsletter" />
                  <label htmlFor="newsletter" className="text-sm text-foreground cursor-pointer">
                    Subscribe to newsletter
                  </label>
                </div>
              </div>
            </ComponentPreview>

            {/* Footer Component */}
            <ComponentPreview 
              title="Footer (Locked Design System Component)" 
              description="Three-column footer with Product, Company, and Funded & Supported sections. External links open in new tabs. Imprint opens modal."
            >
              <div className="-mx-6 -mb-6">
                <div className="border border-border rounded-lg overflow-hidden">
                  <Footer />
                </div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                <div className="flex items-start gap-3">
                  <Icon name="Lock" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Locking Rules (CRITICAL)</h4>
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                      <li>• Footer CANNOT be regenerated by Rocket outside Design System</li>
                      <li>• Footer CANNOT be visually altered on individual pages</li>
                      <li>• Page-level prompts may only reference or toggle visibility</li>
                      <li>• Any changes require: Admin → Design System → Footer</li>
                      <li>• Future Rocket prompts MUST NOT mutate footer UI</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Usage</h4>
                    <code className="text-xs text-primary block mb-2">import Footer from '../../components/Footer'
;</code>
                    <code className="text-xs text-primary block">&lt;Footer /&gt;</code>
                  </div>
                </div>
              </div>
            </ComponentPreview>

            {/* TopNavigation Component */}
            <ComponentPreview 
              title="TopNavigation (Locked Design System Component)" 
              description="Primary navigation with Sleeve logo, navigation links (Home, Product, Use Cases, About, Documentation, Blog, Contact), active state styling, and responsive behavior."
            >
              <div className="-mx-6 -mb-6">
                <div className="border border-border rounded-lg overflow-hidden">
                  <TopNavigation />
                </div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                <div className="flex items-start gap-3">
                  <Icon name="Lock" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Locking Rules (CRITICAL)</h4>
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                      <li>• TopNavigation CANNOT be regenerated by Rocket outside Design System</li>
                      <li>• TopNavigation CANNOT be visually altered by page-level prompts</li>
                      <li>• Page-level prompts may ONLY reference or set active route context</li>
                      <li>• Any visual or structural change requires: Admin → Design System → TopNavigation</li>
                      <li>• Future Rocket prompts MUST NOT mutate navigation UI</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Component Details</h4>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <div>
                        <strong className="text-foreground">Structure:</strong> Left-aligned Sleeve logo (clickable → Home), primary navigation links with active state styling
                      </div>
                      <div>
                        <strong className="text-foreground">Typography:</strong> IBM Plex Sans only, uses global typography tokens, no per-link font overrides
                      </div>
                      <div>
                        <strong className="text-foreground">Colors:</strong> Uses design tokens only (background, text, hover, active, divider, focus states)
                      </div>
                      <div>
                        <strong className="text-foreground">Behavior:</strong> Documentation → internal route, Blog → internal route, Logo → routes to Home
                      </div>
                      <div>
                        <strong className="text-foreground">Responsive:</strong> Desktop horizontal navigation, Tablet/Mobile collapsible menu with toggle icon using brand accent color
                      </div>
                      <div>
                        <strong className="text-foreground">Accessibility:</strong> Keyboard accessible and screen-reader friendly
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Usage</h4>
                    <code className="text-xs text-primary block mb-2">import TopNavigation from '../../components/TopNavigation'
;</code>
                    <code className="text-xs text-primary block">&lt;TopNavigation /&gt;</code>
                  </div>
                </div>
              </div>
            </ComponentPreview>
          </section>

          {/* Section 6: Icon Usage Rules */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Icon Usage Rules</h2>
              <p className="text-sm text-muted-foreground">Icon system guidelines and color application</p>
            </div>

            <IconUsageRules />
          </section>

          {/* Footer Note */}
          <div className="mt-12 p-6 rounded-lg bg-card border border-border">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={20} color="var(--color-primary)" />
              <div>
                <h3 className="text-base font-medium text-foreground mb-2">Design System Integrity</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  This design system is read-only and reflects the current production state of the Sleeve platform. 
                  All tokens, components, and patterns shown here are actively used across the application. 
                  Any modifications to the design system should be coordinated through the development team to maintain consistency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDesignSystem;